// Phase 4B.4 — Knowledge Command Center.
//
// Operational console for the governed knowledge index: health, source
// registry, document inspector, contradiction review, retrieval debugger,
// query simulator, safe maintenance, freshness ops, integrity and performance.
// Nothing here renders reasoning, system prompts, evidence blocks or vectors.
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Search, ShieldAlert, Sparkles, Wrench } from "lucide-react";
import {
  debugKnowledgeRetrieval,
  getIntelligencePerformance,
  getIntelligenceTrace,
  getKnowledgeCommandCenter,
  getKnowledgeSourceRegistry,
  inspectKnowledgeDocumentFn,
  listKnowledgeContradictions,
  listKnowledgeDocumentsFn,
  runKnowledgeMaintenance,
  setKnowledgeContradictionStatus,
  simulateIntelligenceQuery,
} from "@/lib/knowledge.functions";

export const Route = createFileRoute("/_authenticated/admin/knowledge")({
  head: () => ({
    meta: [
      { title: "Knowledge Command Center | KAfrequency Admin" },
      {
        name: "description",
        content:
          "Operate the governed knowledge index: health, sources, documents, contradictions, retrieval diagnostics and safe maintenance.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminKnowledge,
});

const TABS = [
  "Health",
  "Sources",
  "Documents",
  "Contradictions",
  "Debugger",
  "Simulator",
  "Maintenance",
  "Performance",
] as const;
type Tab = (typeof TABS)[number];

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/50 p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-border px-3 py-1 text-xs">{children}</span>;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 rounded-xl border border-border/60 bg-card/40 p-4">
      <h2 className="mb-3 text-sm font-medium">{title}</h2>
      {children}
    </section>
  );
}

function AdminKnowledge() {
  const [tab, setTab] = useState<Tab>("Health");

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-6">
        <h1 className="font-display text-2xl">Knowledge Command Center</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Governed index operations. Diagnostics only — no reasoning, prompts, evidence blocks or
          embedding vectors are ever exposed here.
        </p>
      </header>

      <nav aria-label="Command center sections" className="mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-current={tab === t ? "page" : undefined}
            className={`rounded-full border px-3 py-1.5 text-xs ${
              tab === t ? "border-gold bg-gold/10 text-gold" : "border-border text-muted-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === "Health" ? <HealthTab /> : null}
      {tab === "Sources" ? <SourcesTab /> : null}
      {tab === "Documents" ? <DocumentsTab /> : null}
      {tab === "Contradictions" ? <ContradictionsTab /> : null}
      {tab === "Debugger" ? <DebuggerTab /> : null}
      {tab === "Simulator" ? <SimulatorTab /> : null}
      {tab === "Maintenance" ? <MaintenanceTab /> : null}
      {tab === "Performance" ? <PerformanceTab /> : null}
    </main>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {label}
    </p>
  );
}

function ErrorNote({ error }: { error: unknown }) {
  return (
    <p role="alert" className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
      <ShieldAlert className="h-4 w-4" aria-hidden="true" />
      {(error as Error)?.message ?? "Something went wrong."}
    </p>
  );
}

// ── Health ──────────────────────────────────────────────────────────────────

function HealthTab() {
  const fetchHealth = useServerFn(getKnowledgeCommandCenter);
  const q = useQuery({ queryKey: ["kcc-health"], queryFn: () => fetchHealth() });

  if (q.isLoading) return <Loading label="Loading knowledge index…" />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const d = q.data;
  const empty = d.documents === 0;

  return (
    <>
      {empty ? (
        <p className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          The index is empty. Run a source sync from Maintenance to populate it.
        </p>
      ) : null}

      <section aria-label="Index health" className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Documents" value={String(d.documents)} hint={`${d.sourceTypes} source types`} />
        <Stat label="Active chunks" value={String(d.chunks)} />
        <Stat label="Embeddings" value={`${d.embeddedChunks}/${d.chunks}`} hint={`${d.unembeddedChunks} unembedded`} />
        <Stat label="Retired documents" value={String(d.retiredDocuments)} />
      </section>

      <Panel title="Trust tiers">
        <div className="flex flex-wrap gap-2">
          {d.trustTiers.length ? (
            d.trustTiers.map((t) => <Chip key={t.tier}>tier {t.tier}: {t.documents}</Chip>)
          ) : (
            <p className="text-sm text-muted-foreground">No indexed documents.</p>
          )}
        </div>
      </Panel>

      <Panel title="Freshness distribution">
        <div className="flex flex-wrap gap-2">
          {d.freshness.length ? (
            d.freshness.map((f) => <Chip key={f.state}>{f.state}: {f.documents}</Chip>)
          ) : (
            <p className="text-sm text-muted-foreground">No indexed documents.</p>
          )}
        </div>
      </Panel>

      <Panel title="Sync history">
        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Last successful</p>
            {d.lastSuccessfulSync ? (
              <p className="mt-1 text-muted-foreground">
                {d.lastSuccessfulSync.status}
                {d.lastSuccessfulSync.dry_run ? " (dry run)" : ""} ·{" "}
                {new Date(d.lastSuccessfulSync.started_at).toLocaleString()} ·{" "}
                {d.lastSuccessfulSync.duration_ms ?? "?"}ms · {d.lastSuccessfulSync.documents_seen ?? 0} seen ·{" "}
                {d.lastSuccessfulSync.documents_created ?? 0} created · {d.lastSuccessfulSync.documents_updated ?? 0} updated ·{" "}
                {d.lastSuccessfulSync.documents_retired ?? 0} retired · {d.lastSuccessfulSync.chunks_written ?? 0} chunks ·{" "}
                {d.lastSuccessfulSync.embeddings_written ?? 0} embeddings
              </p>
            ) : (
              <p className="mt-1 text-muted-foreground">No successful sync recorded.</p>
            )}
          </div>
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Last failed / partial</p>
            {d.lastFailedSync ? (
              <p className="mt-1 text-muted-foreground">
                {d.lastFailedSync.status} · {new Date(d.lastFailedSync.started_at).toLocaleString()} ·{" "}
                {d.lastFailedSync.error_code ?? "no code"}
              </p>
            ) : (
              <p className="mt-1 text-muted-foreground">None recorded.</p>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="Integrity & orphans">
        <ul className="space-y-2 text-sm">
          {d.integrity.map((i) => (
            <li key={i.kind} className="rounded-lg border border-border/60 p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span>{i.kind.replace(/_/g, " ")}</span>
                <span className={i.count ? "text-destructive" : "text-muted-foreground"}>{i.count}</span>
              </div>
              {i.ids.length ? (
                <p className="mt-1 break-all text-xs text-muted-foreground">{i.ids.join(", ")}</p>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Contradictions">
        <div className="flex flex-wrap gap-2">
          {d.contradictions.length ? (
            d.contradictions.map((c) => <Chip key={c.status}>{c.status}: {c.count}</Chip>)
          ) : (
            <p className="text-sm text-muted-foreground">None recorded.</p>
          )}
        </div>
      </Panel>

      <Panel title="Recent indexing errors">
        {d.indexingErrors.length ? (
          <ul className="space-y-1 text-xs text-destructive">
            {d.indexingErrors.map((e, i) => (
              <li key={`${e.code}-${i}`}>
                {e.sourceType} · {e.code} · {e.message}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No indexing errors in the last runs.</p>
        )}
      </Panel>
    </>
  );
}

// ── Sources ─────────────────────────────────────────────────────────────────

function SourcesTab() {
  const fetchRegistry = useServerFn(getKnowledgeSourceRegistry);
  const q = useQuery({ queryKey: ["kcc-sources"], queryFn: () => fetchRegistry() });

  if (q.isLoading) return <Loading label="Loading source registry…" />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;

  return (
    <div className="space-y-3">
      {q.data.map((s) => (
        <article key={s.sourceType} className="rounded-xl border border-border/60 bg-card/40 p-4 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-medium">
              {s.sourceType} <span className="text-xs text-muted-foreground">· {s.origin}</span>
            </h3>
            <span
              className={
                s.health === "healthy"
                  ? "text-xs text-emerald-500"
                  : s.health === "error"
                    ? "text-xs text-destructive"
                    : "text-xs text-amber-500"
              }
            >
              {s.health.replace(/_/g, " ")}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            trust {s.trustTier} · {s.provenance} · route {s.routePrefix ?? "—"} · {s.documents} docs · {s.chunks} chunks ·{" "}
            {s.staleDocuments} stale
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            last indexed {s.lastIndexedAt ? new Date(s.lastIndexedAt).toLocaleString() : "never"} · last source change{" "}
            {s.lastModifiedAt ? new Date(s.lastModifiedAt).toLocaleString() : "unknown"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">filter: {s.publicFilter}</p>
          <p className="mt-1 break-all text-xs text-muted-foreground">columns: {s.columns.join(", ")}</p>
          {s.lastError ? <p className="mt-1 text-xs text-destructive">{s.lastError}</p> : null}
        </article>
      ))}
    </div>
  );
}

// ── Documents + inspector ───────────────────────────────────────────────────

function DocumentsTab() {
  const list = useServerFn(listKnowledgeDocumentsFn);
  const inspect = useServerFn(inspectKnowledgeDocumentFn);
  const [sourceType, setSourceType] = useState("");
  const [freshness, setFreshness] = useState("");
  const [search, setSearch] = useState("");
  const [includeRetired, setIncludeRetired] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const docs = useQuery({
    queryKey: ["kcc-docs", sourceType, freshness, search, includeRetired],
    queryFn: () =>
      list({
        data: {
          sourceType: sourceType || null,
          freshness: (freshness || null) as never,
          search: search || null,
          includeRetired,
          limit: 200,
        },
      }),
  });

  const detail = useQuery({
    queryKey: ["kcc-doc", selected],
    queryFn: () => inspect({ data: { sourceId: selected! } }),
    enabled: Boolean(selected),
  });

  return (
    <>
      <Panel title="Filters">
        <div className="flex flex-wrap items-end gap-3 text-xs">
          <label>
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Source type</span>
            <input
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value.trim())}
              placeholder="codex"
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label>
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Freshness</span>
            <select
              value={freshness}
              onChange={(e) => setFreshness(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            >
              <option value="">any</option>
              <option value="fresh">fresh</option>
              <option value="aging">aging</option>
              <option value="stale">stale</option>
              <option value="unknown">unknown</option>
              <option value="superseded">superseded</option>
            </select>
          </label>
          <label className="min-w-[200px] flex-1">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Title contains</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.target.checked)} />
            include retired
          </label>
        </div>
      </Panel>

      <Panel title={`Documents (${docs.data?.length ?? 0})`}>
        {docs.isLoading ? (
          <Loading label="Loading documents…" />
        ) : docs.error ? (
          <ErrorNote error={docs.error} />
        ) : !docs.data?.length ? (
          <p className="text-sm text-muted-foreground">No documents match these filters.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {docs.data.map((d) => (
              <li key={d.sourceId}>
                <button
                  type="button"
                  onClick={() => setSelected(d.sourceId)}
                  className={`w-full rounded-lg border p-2 text-left ${
                    selected === d.sourceId ? "border-gold" : "border-border/60"
                  }`}
                >
                  <span className="font-medium">{d.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {d.sourceId} · {d.chunkCount} chunks · {d.freshness}
                    {d.retired ? " · retired" : ""} — {d.freshnessReason}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {selected ? (
        <Panel title="Document inspector">
          {detail.isLoading ? (
            <Loading label="Loading document…" />
          ) : detail.error ? (
            <ErrorNote error={detail.error} />
          ) : !detail.data ? (
            <p className="text-sm text-muted-foreground">Document not found.</p>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="font-medium">{detail.data.title}</p>
              <p className="text-xs text-muted-foreground">
                {detail.data.sourceId} · doc {detail.data.documentId} · {detail.data.sourceType} · trust{" "}
                {detail.data.trustTier} · {detail.data.provenance} · extraction v{detail.data.extractionVersion}
              </p>
              <p className="text-xs text-muted-foreground">
                path {detail.data.canonicalPath ?? "—"} · url {detail.data.canonicalUrl ?? "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                source modified {detail.data.sourceModifiedAt ?? "unknown"} · indexed {detail.data.indexedAt ?? "unknown"} ·
                hash {detail.data.contentHash.slice(0, 16)}… ·{" "}
                {detail.data.retiredAt ? `retired ${detail.data.retiredAt}` : "active"}
              </p>
              <p className="text-xs text-muted-foreground">
                freshness {detail.data.freshness.state} — {detail.data.freshness.reason}
              </p>
              <p className="text-xs text-muted-foreground">
                entities: {detail.data.entityIds.join(", ") || "—"} · concepts:{" "}
                {detail.data.conceptIds.join(", ") || "—"}
              </p>
              {detail.data.relations.length ? (
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {detail.data.relations.map((r, i) => (
                    <li key={`${r.kind}-${r.ref}-${i}`}>
                      {r.kind} → {r.ref} ({r.label}) — {r.reason}
                    </li>
                  ))}
                </ul>
              ) : null}

              <h3 className="pt-2 text-xs uppercase tracking-wider text-muted-foreground">
                Chunks ({detail.data.chunks.length} of {detail.data.chunkCount})
              </h3>
              <ol className="space-y-2">
                {detail.data.chunks.map((c) => (
                  <li key={c.chunkId} className="rounded-lg border border-border/60 p-3">
                    <p className="text-xs text-muted-foreground">
                      #{c.chunkIndex} · {c.heading ?? "no heading"} · ~{c.tokenEstimate} tokens · hash{" "}
                      {c.contentHash.slice(0, 12)}… · {c.hasEmbedding ? c.embeddingModel : "not embedded"}
                      {c.embeddedAt ? ` · ${new Date(c.embeddedAt).toLocaleString()}` : ""}
                    </p>
                    <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{c.preview}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </Panel>
      ) : null}
    </>
  );
}

// ── Contradictions ──────────────────────────────────────────────────────────

function ContradictionsTab() {
  const listFn = useServerFn(listKnowledgeContradictions);
  const setStatus = useServerFn(setKnowledgeContradictionStatus);
  const qc = useQueryClient();
  const [status, setStatusFilter] = useState<string>("");

  const q = useQuery({
    queryKey: ["kcc-contradictions", status],
    queryFn: () => listFn({ data: { status: (status || null) as never } }),
  });

  const update = useMutation({
    mutationFn: (input: { fingerprint: string; status: string }) =>
      setStatus({ data: { fingerprint: input.fingerprint, status: input.status as never } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kcc-contradictions"] });
      void qc.invalidateQueries({ queryKey: ["kcc-health"] });
    },
  });

  return (
    <>
      <Panel title="Filter">
        <select
          value={status}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        >
          <option value="">all</option>
          <option value="open">open</option>
          <option value="acknowledged">acknowledged</option>
          <option value="resolved">resolved</option>
          <option value="false_positive">false positive</option>
        </select>
        <p className="mt-2 text-xs text-muted-foreground">
          Reviewing a contradiction records a decision only. It never edits the canonical source.
        </p>
      </Panel>

      {q.isLoading ? (
        <Loading label="Loading contradictions…" />
      ) : q.error ? (
        <ErrorNote error={q.error} />
      ) : !q.data?.length ? (
        <p className="text-sm text-muted-foreground">No contradictions recorded.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {q.data.map((c) => (
            <li key={c.fingerprint} className="rounded-xl border border-border/60 bg-card/40 p-4">
              <p>
                <strong>{c.subject}</strong> — {c.kind} ({c.severity}) · {c.status}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                canonical “{c.canonical_value}” ({c.canonical_source_id ?? "—"}) vs “{c.conflicting_value}” (
                {c.conflicting_source_id ?? c.conflicting_url ?? "—"})
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                first detected {new Date(c.detected_at).toLocaleString()} · last seen{" "}
                {new Date(c.last_seen_at).toLocaleString()}
                {c.resolved_at ? ` · resolved ${new Date(c.resolved_at).toLocaleString()}` : ""}
                {c.resolution_note ? ` · ${c.resolution_note}` : ""}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {["open", "acknowledged", "resolved", "false_positive"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={update.isPending || c.status === s}
                    onClick={() => update.mutate({ fingerprint: c.fingerprint, status: s })}
                    className="rounded-full border border-border px-3 py-1 text-xs disabled:opacity-40"
                  >
                    {s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Retrieval debugger 2.0 ──────────────────────────────────────────────────

function DebuggerTab() {
  const runDebug = useServerFn(debugKnowledgeRetrieval);
  const fetchTrace = useServerFn(getIntelligenceTrace);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(8);
  const [skipSemantic, setSkipSemantic] = useState(false);
  const [enableGraph, setEnableGraph] = useState(true);

  const trace = useQuery({ queryKey: ["intelligence-trace"], queryFn: () => fetchTrace() });
  const debugRun = useMutation({
    mutationFn: () => runDebug({ data: { query, limit, skipSemantic, enableGraph } }),
  });
  const pack = debugRun.data;

  return (
    <>
      <Panel title="Run a retrieval">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (query.trim().length >= 2) debugRun.mutate();
          }}
        >
          <label className="min-w-[240px] flex-1 text-xs">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Query</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What is stillness in the Codex?"
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Limit</span>
            <input
              type="number"
              min={1}
              max={25}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!skipSemantic} onChange={(e) => setSkipSemantic(!e.target.checked)} />
            Semantic lane
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={enableGraph} onChange={(e) => setEnableGraph(e.target.checked)} />
            Graph hop
          </label>
          <button
            type="submit"
            disabled={debugRun.isPending || query.trim().length < 2}
            className="inline-flex items-center gap-2 rounded-md bg-gold px-3 py-1.5 text-sm text-background disabled:opacity-50"
          >
            {debugRun.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Search className="h-4 w-4" aria-hidden="true" />
            )}
            Retrieve
          </button>
        </form>

        {debugRun.isError ? <p role="alert" className="mt-3 text-sm text-destructive">{(debugRun.error as Error).message}</p> : null}

        {pack ? (
          <div className="mt-5 space-y-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Chip>intent: {pack.query.intent}</Chip>
              <Chip>mode: {pack.query.retrievalMode}</Chip>
              <Chip>coverage: {pack.coverage}</Chip>
              <Chip>confidence: {Math.round(pack.confidence * 100)}%</Chip>
              <Chip>{pack.totalMs}ms</Chip>
              <Chip>{pack.candidatesConsidered} candidates</Chip>
              <Chip>specificity: {pack.query.specificity.toFixed(2)}</Chip>
              {pack.query.vague ? <Chip>vague</Chip> : null}
            </div>

            <p className="text-xs text-muted-foreground">
              entities: {pack.entities.map((e) => `${e.name} (${e.method}${e.ambiguous ? ", ambiguous" : ""})`).join(", ") || "—"} ·
              concepts: {pack.query.concepts.join(", ") || "—"} · exact terms: {pack.query.exactTerms.join(", ") || "—"}
            </p>

            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {pack.lanes.map((l) => (
                <span key={l.lane} className="rounded border border-border/60 px-2 py-1">
                  {l.lane}: {l.ok ? `${l.hits} hits` : l.attempted ? `failed${l.error ? ` (${l.error})` : ""}` : "skipped"} · {l.ms}ms
                </span>
              ))}
            </div>

            {pack.degraded.length ? (
              <ul className="space-y-1 text-xs text-amber-500">
                {pack.degraded.map((d) => (
                  <li key={d}>⚠ {d}</li>
                ))}
              </ul>
            ) : null}

            {pack.contradictions.length ? (
              <ul className="space-y-1 text-xs">
                {pack.contradictions.map((c) => (
                  <li key={c.fingerprint} className="rounded border border-destructive/30 bg-destructive/5 p-2">
                    <strong>{c.subject}</strong> {c.kind}: canonical “{c.canonicalValue}” vs “{c.conflictingValue}” ({c.severity})
                  </li>
                ))}
              </ul>
            ) : null}

            <ol className="space-y-2">
              {pack.hits.map((h, i) => (
                <li key={`${h.sourceId}-${h.chunkId ?? i}`} className="rounded-lg border border-border/60 p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-medium">
                      {i + 1}. {h.title}
                      {h.heading ? <span className="text-muted-foreground"> — {h.heading}</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">score {h.score.toFixed(3)}</p>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {h.sourceId} · trust {h.trustTier} · {h.provenance} · {h.freshness} · lanes {h.lanes.join("+")} · ranks{" "}
                    {Object.entries(h.laneRanks).map(([lane, rank]) => `${lane}#${rank}`).join(" ") || "—"}
                    {h.relationReason ? ` · via ${h.relationReason}` : ""}
                    {h.contradiction ? " · contradiction" : ""}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    fusion {h.components.fusionScore.toFixed(4)} · trust {h.components.trustAdjustment.toFixed(2)} · fresh{" "}
                    {h.components.freshnessAdjustment.toFixed(2)} · exact {h.components.exactnessAdjustment.toFixed(2)} · rel{" "}
                    {h.components.relationAdjustment.toFixed(2)} · lane agreement {h.lanes.length}
                  </p>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{h.content.slice(0, 320)}</p>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </Panel>

      <Panel title="Intelligence trace (last 30 runs)">
        <p className="mb-3 text-xs text-muted-foreground">
          Shape only — question class, retrieval decision, evidence ids and timings. No prompts, no answers,
          no visitor text is stored or shown here.
        </p>
        {trace.isLoading ? (
          <Loading label="Loading trace…" />
        ) : !trace.data?.length ? (
          <p className="text-sm text-muted-foreground">No orchestrated runs recorded yet.</p>
        ) : (
          <ul className="space-y-1 text-xs text-muted-foreground">
            {trace.data.map((r) => (
              <li key={r.id} className="rounded border border-border/60 p-2">
                {new Date(r.created_at).toLocaleString()} · {r.intent ?? "—"} · {r.answer_mode ?? "—"} ·{" "}
                {r.confidence_bucket ?? "—"} · {r.evidence_count ?? 0} evidence · {r.retrieval_ms ?? 0}ms retrieval ·{" "}
                {r.latency_ms ?? 0}ms total · {r.status ?? "—"}
                {r.grounding_flagged ? " · grounding flagged" : ""}
                {r.contradiction ? " · contradiction" : ""}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </>
  );
}

// ── Simulator ───────────────────────────────────────────────────────────────

function SimulatorTab() {
  const simulate = useServerFn(simulateIntelligenceQuery);
  const [query, setQuery] = useState("Karayasataahla");
  const run = useMutation({ mutationFn: () => simulate({ data: { query, generate: true } }) });
  const r = run.data;

  return (
    <Panel title="Query simulator">
      <p className="mb-3 text-xs text-muted-foreground">
        Runs the exact production orchestration path as an anonymous visitor. Nothing is written to
        conversations, memory or telemetry.
      </p>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (query.trim().length >= 2) run.mutate();
        }}
      >
        <label className="min-w-[260px] flex-1 text-xs">
          <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Query</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={run.isPending || query.trim().length < 2}
          className="inline-flex items-center gap-2 rounded-md bg-gold px-3 py-1.5 text-sm text-background disabled:opacity-50"
        >
          {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="h-4 w-4" aria-hidden="true" />}
          Simulate
        </button>
      </form>

      {run.isError ? <p role="alert" className="mt-3 text-sm text-destructive">{(run.error as Error).message}</p> : null}

      {r ? (
        <div className="mt-5 space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Chip>intent: {r.intent}</Chip>
            <Chip>role: {r.role}</Chip>
            <Chip>route: {r.modelRoute}</Chip>
            <Chip>mode: {r.answerMode}</Chip>
            <Chip>confidence: {r.confidence}</Chip>
            <Chip>evidence: {r.evidenceQuality} ({r.evidenceCount})</Chip>
            <Chip>grounding: {r.grounding}</Chip>
            <Chip>{r.retrievalMs}ms retrieval</Chip>
            <Chip>{r.totalMs}ms total</Chip>
          </div>
          <p className="text-xs text-muted-foreground">
            entities: {r.entities.map((e) => `${e.name} (${e.kind}/${e.method})`).join(", ") || "—"} · concepts:{" "}
            {r.concepts.join(", ") || "—"}
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {r.lanes.map((l) => (
              <span key={l.lane} className="rounded border border-border/60 px-2 py-1">
                {l.lane}: {l.ok ? `${l.hits} hits` : l.attempted ? "failed" : "skipped"} · {l.ms}ms
              </span>
            ))}
          </div>
          {r.degraded.length ? (
            <ul className="space-y-1 text-xs text-amber-500">
              {r.degraded.map((d) => (
                <li key={d}>⚠ {d}</li>
              ))}
            </ul>
          ) : null}
          {r.contradictions.length ? (
            <ul className="space-y-1 text-xs">
              {r.contradictions.map((c, i) => (
                <li key={`${c.subject}-${i}`} className="rounded border border-destructive/30 bg-destructive/5 p-2">
                  <strong>{c.subject}</strong> {c.kind}: “{c.canonicalValue}” vs “{c.conflictingValue}” ({c.severity})
                </li>
              ))}
            </ul>
          ) : null}
          <ol className="space-y-1 text-xs text-muted-foreground">
            {r.hits.map((h, i) => (
              <li key={`${h.sourceId}-${i}`}>
                {i + 1}. {h.title} · {h.sourceId} · trust {h.trustTier} · {h.freshness} · {h.score.toFixed(3)}
              </li>
            ))}
          </ol>
          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Sources</h3>
            <ul className="text-xs text-muted-foreground">
              {r.sources.length ? r.sources.map((s) => <li key={s.url}>{s.title} — {s.url}</li>) : <li>—</li>}
            </ul>
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Answer</h3>
            <p className="mt-1 whitespace-pre-wrap rounded-lg border border-border/60 p-3 text-sm">{r.answer || "—"}</p>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

// ── Maintenance ─────────────────────────────────────────────────────────────

function MaintenanceTab() {
  const runFn = useServerFn(runKnowledgeMaintenance);
  const registryFn = useServerFn(getKnowledgeSourceRegistry);
  const qc = useQueryClient();
  const registry = useQuery({ queryKey: ["kcc-sources"], queryFn: () => registryFn() });
  const [sourceType, setSourceType] = useState("");
  const [sourceId, setSourceId] = useState("");

  const run = useMutation({
    mutationFn: (input: { action: string; sourceType?: string | null; sourceId?: string | null }) =>
      runFn({ data: input as never }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kcc-health"] });
      void qc.invalidateQueries({ queryKey: ["kcc-sources"] });
      void qc.invalidateQueries({ queryKey: ["kcc-docs"] });
    },
  });

  const button = (label: string, input: { action: string; sourceType?: string | null; sourceId?: string | null }) => (
    <button
      type="button"
      disabled={run.isPending}
      onClick={() => run.mutate(input)}
      className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-50"
    >
      <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </button>
  );

  return (
    <>
      <Panel title="Full index">
        {button("Dry-run full sync", { action: "dry_run_all" })}
      </Panel>

      <Panel title="One approved source">
        <div className="flex flex-wrap items-end gap-3 text-xs">
          <label>
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Source type</span>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            >
              <option value="">select…</option>
              {(registry.data ?? []).map((s) => (
                <option key={s.sourceType} value={s.sourceType}>
                  {s.sourceType}
                </option>
              ))}
            </select>
          </label>
          {button("Sync source", { action: "sync_source", sourceType })}
        </div>
      </Panel>

      <Panel title="One document">
        <div className="flex flex-wrap items-end gap-3 text-xs">
          <label className="min-w-[260px] flex-1">
            <span className="mb-1 block uppercase tracking-wider text-muted-foreground">Source id</span>
            <input
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value.trim())}
              placeholder="codex:stillness"
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          {button("Reindex", { action: "reindex_document", sourceId })}
          {button("Regenerate embeddings", { action: "reembed_document", sourceId })}
          {button("Retire", { action: "retire_document", sourceId })}
          {button("Restore / re-sync", { action: "restore_document", sourceId })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Restore re-runs the exact ingestion governance. A row that no longer satisfies its public
          filter is retired instead of restored.
        </p>
      </Panel>

      {run.isPending ? <Loading label="Running maintenance…" /> : null}
      {run.data ? (
        <p
          role="status"
          className={`rounded-lg border p-3 text-sm ${
            run.data.ok ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/40 bg-destructive/10"
          }`}
        >
          {run.data.action}
          {run.data.scope ? ` · ${run.data.scope}` : ""} — {run.data.message}
        </p>
      ) : null}
      {run.isError ? <ErrorNote error={run.error} /> : null}
    </>
  );
}

// ── Performance ─────────────────────────────────────────────────────────────

function PerformanceTab() {
  const fetchPerf = useServerFn(getIntelligencePerformance);
  const q = useQuery({ queryKey: ["kcc-perf"], queryFn: () => fetchPerf() });

  if (q.isLoading) return <Loading label="Loading performance…" />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const p = q.data;
  const ms = (v: number | null) => (v == null ? "—" : `${v}ms`);
  const pct = (v: number) => `${Math.round(v * 100)}%`;

  return (
    <>
      <section className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Samples" value={String(p.samples)} hint="most recent orchestrated runs" />
        <Stat label="Retrieval p50" value={ms(p.retrievalP50)} />
        <Stat label="Retrieval p95" value={ms(p.retrievalP95)} />
        <Stat label="Retrieval p99" value={ms(p.retrievalP99)} hint={p.retrievalP99 == null ? "sample too small" : undefined} />
        <Stat label="Orchestration p50" value={ms(p.orchestrationP50)} />
        <Stat label="Orchestration p95" value={ms(p.orchestrationP95)} />
        <Stat label="Grounding failures" value={`${p.groundingFailures} (${pct(p.groundingFailureRate)})`} />
        <Stat label="Degradation rate" value={pct(p.degradationRate)} />
        <Stat label="Not-found rate" value={pct(p.notFoundRate)} />
        <Stat label="Contradiction rate" value={pct(p.contradictionRate)} />
      </section>

      <Panel title="Lane usage">
        <div className="flex flex-wrap gap-2">
          {p.laneUsage.length ? (
            p.laneUsage.map((l) => <Chip key={l.lane}>{l.lane}: {l.runs} runs</Chip>)
          ) : (
            <p className="text-sm text-muted-foreground">No lane telemetry yet.</p>
          )}
        </div>
      </Panel>
    </>
  );
}
